import React from 'react';
import styled from 'styled-components';
import PALSLogo from './PALS_logo.png';
import MedExplainLogo from './MedExplain_logo.png';
import Eat54321Logo from './EAT54321.png';

const ClientsContainer = styled.div`
   padding: 60px 120px 40px;
   display: flex;
   flex-direction: column;
   justify-content: center;
   background: #ffffff;
   overflow-x: auto;

   @media (max-width: 768px) {
       align-items: center;
       padding: 30px 20px;
       margin-bottom: 195px;
   }
`;

const ClientInner = styled.div`
   display: flex;
   justify-content: center;
   align-items: center;
   gap: 40px;
   width: 100%;

   @media (max-width: 768px) {
       flex-direction: column;
       gap: 20px;
   }
`;

const ClientItem = styled.div`
   border: 2px solid #e0e0e0;
   border-radius: 8px;
   padding: 16px;
   display: flex;
   align-items: center;
   justify-content: center;
   transition: transform 0.3s, box-shadow 0.3s;
   width: 240px;
   height: 120px;

   &:hover {
       transform: translateY(-5px);
       box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
   }

   img {
       max-width: 100%;
       max-height: 100%;
       object-fit: contain;
   }

   span {
       font-size: 24px;
       font-weight: bold;
       color: #800020;
   }
`;

const TestimonialsContainer = styled.div`
   margin-top: 60px;
   padding: 40px 120px;
   background: #f9f9f9;
   display: flex;
   flex-direction: column;  // Stack vertically
   align-items: center;
   gap: 30px;  // Add space between testimonials

   @media (max-width: 768px) {
     padding: 30px 20px;
   }
`;

const SectionLabel = styled.h2`
   font-size: 22px;
   font-weight: 600;
   color: #5b4b8a;
   margin: 0;
   text-align: center;
`;

const Attribution = styled.div`
   margin-top: 14px;
   font-style: normal;
   font-size: 15px;
   color: #555;

   strong {
       display: block;
       font-size: 16px;
       color: #333;
   }

   em {
       display: block;
       margin-top: 4px;
       font-size: 14px;
       color: #777;
   }
`;

const FounderNote = styled.div`
   max-width: 800px;
   font-size: 17px;
   color: #333;
   line-height: 1.6;
   background: #ffffff;
   border-left: 4px solid #800020;
   padding: 20px;
   box-shadow: 0 2px 6px rgba(0,0,0,0.1);
   border-radius: 6px;
`;

const TestimonialItem = styled.div`
   max-width: 800px;  // Slightly wider for readability
   font-style: italic;
   font-size: 18px;
   color: #333;
   border-left: 4px solid #5b4b8a;
   padding-left: 20px;
   line-height: 1.6;
   background: #ffffff; // White background to pop from grey
   padding: 20px; // Uniform padding for a card look
   box-shadow: 0 2px 6px rgba(0,0,0,0.1);
   border-radius: 6px;
`;

const testimonials = [
    {
        quote: "Susan has created and implemented our entire technology stack at MedExplain Health, designing the platform from the ground up, standing up a React front end with MSAL secured admin tools, a Cosmos DB backend, Algolia search, and image handling in Azure Blob Storage. She built our one-click AI translation system that breaks long articles into translation-safe segments, sends them through Azure Translator with the correct settings, then rebuilds them exactly as written with preserved formatting, headings, and citations. She also added validation checks, fallback handling, and automatic re-translation so our team can publish accurate multilingual content with a single click. She has layered in automated backups, Cloudflare performance tuning, and a CI/CD pipeline that lets our team push updates with confidence.\n\nSusan has gone above and beyond, also building us a custom SharePoint workspace to support our content workflow, mentoring interns working on our React Native prototype, and continuously refining the user experience based on feedback from our team and end users. Her mix of expert technical skill, strategic vision, and dedication to our mission has made her an ideal partner for MedExplain Health.\n\nI recommend Susan and Zumosoft without hesitation to anyone who needs an end-to-end technology partner who delivers excellent, high-quality results.",
        author: "Ariel Barasch",
        title: "Executive Director, MedExplain Health",
        context: "Worked with Susan directly at MedExplain Health"
    },
    {
        quote: "Susan is a strong, passionate professional who has outstanding qualities for organization, handling of multiple projects and personnel, and strong technical and analytical skills. She is compassionate and sincere while also having the strength to handle changes and coaching when needed. She is an amazing asset to her team and her organization. It is an honor to work for her.",
        author: "Arleta Greer",
        title: "Lead IT Business Analyst, Little Caesars Pizza",
        context: "Reported to Susan directly"
    },
    {
        quote: "Susan is very focused and passionate about technology. I have seen her take it upon herself to enhance and inspire a company to invest in themselves to help advance a new mobility practice. Susan is great at delivering solutions and knows how to get through tough situations. Susan is a leader in the community and often challenges herself to take on speaking engagements in multiple venues throughout the year.",
        author: "Dave Wollerman",
        title: "Sr. Cloud Solution Architect, Microsoft",
        context: "Worked with Susan on the same team at Sogeti"
    }
];

// Susan's own account of EAT 54321. Deliberately separated from the
// testimonials above: it is a founder's note, not a third-party reference, and
// presenting it as one would undercut the references that are.
const founderNote = {
    quote: "Creating EAT 54321 has been a passion project and professional dream of mine for over two decades. Through Zumosoft, I've leveraged my expertise in modern web technologies, including React, Azure, and Cosmos DB, to develop a scalable, user-friendly platform that genuinely helps people improve their health. Zumosoft doesn't just build software; we craft digital solutions that combine robust technical skill with real-world impact.",
    author: "Susan Anspaugh, Founder of Zumosoft and EAT 54321"
};

function Clients() {
    return (
        <ClientsContainer>
            <ClientInner>
                <ClientItem>
                    <a href="https://www.medexplain.org" target="_blank" rel="noopener noreferrer">
                        <img src={MedExplainLogo} alt="MedExplain Logo" />
                    </a>
                </ClientItem>
                <ClientItem>
                    <span>Weill Cornell</span>
                </ClientItem>
                <ClientItem>
                    <a href="https://palsforhealth.com" target="_blank" rel="noopener noreferrer">
                        <img src={PALSLogo} alt="PALS Logo" />
                    </a>
                </ClientItem>
                <ClientItem>
                    <a href="https://eat54321.com" target="_blank" rel="noopener noreferrer">
                        <img src={Eat54321Logo} alt="Eat54321 Logo" />
                    </a>
                </ClientItem>
            </ClientInner>

            <TestimonialsContainer>
                <SectionLabel>What clients and colleagues say</SectionLabel>
                {testimonials.map((testimonial, index) => (
                    <TestimonialItem key={index}>
                        {testimonial.quote}
                        <Attribution>
                            <strong>{testimonial.author}</strong>
                            {testimonial.title}
                            <em>{testimonial.context}</em>
                        </Attribution>
                    </TestimonialItem>
                ))}

                <SectionLabel>From the founder</SectionLabel>
                <FounderNote>
                    {founderNote.quote}
                    <Attribution>
                        <strong>{founderNote.author}</strong>
                    </Attribution>
                </FounderNote>
            </TestimonialsContainer>
        </ClientsContainer>
    );
}

export default Clients;
